import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ShoppingBag, User, Menu, X, Mail, MessageCircle } from "lucide-react";
import { LogoFull } from "@/components/Logo";
import { useApp } from "@/context/AppContext";
import { CONTACT } from "@/data/siteConfig";

export const Navbar = () => {
  const { t, lang, setLang, cartCount, setCartOpen } = useApp();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const linkCls = ({ isActive }) =>
    `nav-link font-mono text-[0.7rem] tracking-[0.3em] uppercase transition-colors duration-200 ${
      isActive ? "is-active text-[#C9A36B]" : "text-white/85 hover:text-[#C9A36B]"
    }`;

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled ? "backdrop-blur-xl bg-[#170F09]/85 border-b border-white/10" : "bg-transparent"
      }`}
    >
      {/* Utility bar — quick contact access, sits above the main row */}
      <div className={`hidden md:block border-b border-white/8 transition-all duration-300 overflow-hidden ${scrolled ? "max-h-0 opacity-0 border-b-0" : "max-h-10 opacity-100"}`}>
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 h-9 flex items-center justify-end gap-6">
          <a href={CONTACT.emailHref} className="flex items-center gap-1.5 text-white/55 hover:text-[#C9A36B] transition-colors text-[0.65rem] font-mono tracking-widest uppercase">
            <Mail size={12} strokeWidth={1.6} /> {CONTACT.email}
          </a>
          <span className="w-px h-3 bg-white/15" />
          <a href={CONTACT.whatsappHref} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-white/55 hover:text-[#C9A36B] transition-colors text-[0.65rem] font-mono tracking-widest uppercase">
            <MessageCircle size={12} strokeWidth={1.6} /> WhatsApp
          </a>
        </div>
      </div>

      <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-5 grid grid-cols-[1fr_auto_1fr] items-center gap-4">
        {/* Left: first half of nav + language */}
        <div className="flex items-center gap-8">
          <nav className="hidden lg:flex items-center gap-8">
            <NavLink to="/" end className={linkCls} data-testid="nav-home">{t.nav.home}</NavLink>
            <NavLink to="/about" className={linkCls} data-testid="nav-about">{t.nav.about}</NavLink>
          </nav>
          <button
            data-testid="mobile-menu-toggle"
            className="icon-btn lg:hidden text-white"
            onClick={() => setMobileOpen(o => !o)}
            aria-label="Menu"
          >
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

        {/* Center: logo */}
        <Link to="/" data-testid="nav-logo" className="shrink-0 justify-self-center">
          <LogoFull size={58} />
        </Link>

        {/* Right: second half of nav + icons */}
        <div className="flex items-center justify-end gap-6 md:gap-8">
          <nav className="hidden lg:flex items-center gap-8">
            <NavLink to="/collection" className={linkCls} data-testid="nav-collection">{t.nav.collection}</NavLink>
            <NavLink to="/configurator" className={linkCls} data-testid="nav-configurator">{t.nav.configurator}</NavLink>
            <NavLink to="/contact" className={linkCls} data-testid="nav-contact">{t.nav.contact}</NavLink>
          </nav>

          <div className="hidden md:flex items-center gap-3">
            <button
              data-testid="lang-toggle-de"
              onClick={() => setLang("de")}
              className={`font-mono text-[0.7rem] tracking-[0.2em] transition-colors ${lang === "de" ? "text-[#C9A36B]" : "text-white/50 hover:text-white/80"}`}
            >DE</button>
            <span className="text-white/20 text-xs">/</span>
            <button
              data-testid="lang-toggle-en"
              onClick={() => setLang("en")}
              className={`font-mono text-[0.7rem] tracking-[0.2em] transition-colors ${lang === "en" ? "text-[#C9A36B]" : "text-white/50 hover:text-white/80"}`}
            >EN</button>
          </div>

          <button
            data-testid="account-button"
            aria-label="Account"
            className="icon-btn text-white/85 hidden md:inline-flex"
          >
            <User size={18} strokeWidth={1.4} />
          </button>

          <button
            data-testid="cart-button"
            onClick={() => setCartOpen(true)}
            aria-label="Cart"
            className="icon-btn text-white/85 relative"
          >
            <ShoppingBag size={18} strokeWidth={1.4} />
            {cartCount > 0 && (
              <span
                data-testid="cart-count"
                className="absolute -top-2 -right-2 bg-[#C9A36B] text-[#170F09] text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center"
              >{cartCount}</span>
            )}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div data-testid="mobile-menu" className="lg:hidden bg-[#170F09]/98 backdrop-blur-xl border-t border-white/10">
          <nav className="flex flex-col p-8 gap-6">
            <NavLink to="/" end className={linkCls} data-testid="mobile-nav-home">{t.nav.home}</NavLink>
            <NavLink to="/about" className={linkCls} data-testid="mobile-nav-about">{t.nav.about}</NavLink>
            <NavLink to="/collection" className={linkCls} data-testid="mobile-nav-collection">{t.nav.collection}</NavLink>
            <NavLink to="/configurator" className={linkCls} data-testid="mobile-nav-configurator">{t.nav.configurator}</NavLink>
            <NavLink to="/contact" className={linkCls} data-testid="mobile-nav-contact">{t.nav.contact}</NavLink>
          </nav>
          <div className="flex items-center gap-6 px-8 pb-8 pt-2 border-t border-white/10">
            <a
              href={CONTACT.emailHref}
              data-testid="mobile-contact-email"
              aria-label="E-Mail"
              className="flex items-center gap-2 text-white/70 hover:text-[#C9A36B] transition-colors text-xs font-mono tracking-widest uppercase"
            >
              <Mail size={16} strokeWidth={1.4} /> E-Mail
            </a>
            <a
              href={CONTACT.whatsappHref}
              target="_blank" rel="noopener noreferrer"
              data-testid="mobile-contact-whatsapp"
              aria-label="WhatsApp"
              className="flex items-center gap-2 text-white/70 hover:text-[#C9A36B] transition-colors text-xs font-mono tracking-widest uppercase"
            >
              <MessageCircle size={16} strokeWidth={1.4} /> WhatsApp
            </a>
          </div>
        </div>
      )}
    </header>
  );
};
